import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

/**
 * Custom dropdown that replaces native <select> so the open list can be fully styled.
 * The option panel renders in a portal (position: fixed) so it never gets clipped by
 * modals or overflow containers.
 *
 * Props:
 *  - value        current value
 *  - onChange     (newValue) => void
 *  - options      array of { value, label } or plain strings/numbers
 *  - placeholder  text shown when nothing selected
 *  - disabled     boolean
 *  - searchable   true  → always show the search box
 *                 false → never show it
 *                 (omitted) → auto: shown once the list has more than 6 options
 */
export default function Select({ value, onChange, options = [], placeholder = 'Select…', disabled = false, searchable }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, bottom: 0, left: 0, width: 0, optHeight: 240, openUp: false });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const opts = useMemo(() => options.map(o =>
    (typeof o === 'string' || typeof o === 'number') ? { value: o, label: String(o) } : o
  ), [options]);
  const selected = opts.find(o => String(o.value) === String(value));

  // Show the search box when explicitly requested, or (auto) for longer lists.
  const showSearch = searchable === true || (searchable !== false && opts.length > 6);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter(o => String(o.label).toLowerCase().includes(q));
  }, [opts, query]);

  const place = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    const gap = 6;
    const vh = window.innerHeight;
    const spaceBelow = vh - r.bottom - gap;
    const spaceAbove = r.top - gap;
    // Flip the panel above the trigger when there isn't enough room below it
    // (e.g. the field sits near the bottom of the screen).
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    const avail = (openUp ? spaceAbove : spaceBelow) - 12;
    const optHeight = Math.max(120, Math.min(avail - (showSearch ? 50 : 0), 300));
    setCoords({
      openUp,
      top: r.bottom + gap,
      bottom: vh - r.top + gap,
      left: r.left,
      width: r.width,
      optHeight,
    });
  };

  useLayoutEffect(() => { if (open) place(); }, [open]);

  // Reset the query each time the panel opens and focus the search field.
  useEffect(() => {
    if (open) {
      setQuery('');
      if (showSearch) setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [open, showSearch]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onReflow = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open]);

  const commit = (v) => { onChange(v); setOpen(false); };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length) commit(filtered[0].value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="cs-wrap" ref={triggerRef}>
      <button
        type="button"
        className={`cs-trigger${open ? ' open' : ''}`}
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
      >
        <span className={selected ? 'cs-value' : 'cs-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={16} className={`cs-chevron${open ? ' open' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="cs-panel"
          style={{
            position: 'fixed', left: coords.left, width: coords.width, right: 'auto', zIndex: 2000,
            ...(coords.openUp
              ? { top: 'auto', bottom: coords.bottom, transformOrigin: 'bottom' }
              : { top: coords.top, bottom: 'auto' }),
          }}
        >
          {showSearch && (
            <div className="cs-search">
              <Search size={14} className="cs-search-icon" />
              <input
                ref={searchRef}
                type="text"
                className="cs-search-input"
                placeholder="Search…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
              />
            </div>
          )}
          <div className="cs-options" style={{ maxHeight: coords.optHeight }}>
            {filtered.length === 0 ? (
              <div className="cs-empty">No matches</div>
            ) : filtered.map((o, i) => {
              const isSel = String(o.value) === String(value);
              return (
                <div
                  key={`${o.value}-${i}`}
                  className={`cs-option${isSel ? ' selected' : ''}`}
                  onClick={() => commit(o.value)}
                >
                  <span>{o.label}</span>
                  {isSel && <Check size={15} className="cs-check" />}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
